/**
 * The design system's identity tints, in the order the monogram avatars cycle
 * them. Gold carries ink text and the rest carry paper, which the Avatar's
 * `tint` variant already handles — so every rung is legible as a monogram.
 * `neutral` is deliberately absent: it's the "no identity" tile, not a colour
 * anyone should be assigned.
 */
const AVATAR_TINTS = ["rust", "sage", "teal", "plum", "gold"] as const;

export type AvatarTint = (typeof AVATAR_TINTS)[number];

/**
 * Picks a stable identity tint for a person from any durable key — their email
 * is the usual one, since it survives display-name edits.
 *
 * The point is only that the same person is always the same colour, in every
 * list, on every device, with no stored preference. FNV-1a is used because it
 * spreads short similar strings (a@co, b@co) across the ladder rather than
 * clumping them the way a length- or charcode-sum would.
 */
export function getAvatarTint(key: string | null | undefined): AvatarTint {
  const normalized = (key ?? "").trim().toLowerCase();
  if (!normalized) return AVATAR_TINTS[0];

  let hash = 0x811c9dc5;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    // The FNV prime, as shifts — `Math.imul` keeps it in 32-bit range.
    hash = Math.imul(hash, 0x01000193);
  }

  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length];
}
